// GoalsSidePanel.jsx — the Goal tab's left column: every goal this user may
// see, filtered Active / All / Completed (Active first), and "+ New Goal".
// Clicking a goal opens it in the middle.
import React, { useState } from 'react';
import { goalStatus, progressOf, isOpen, byDue, fmtDay, stampDay } from './goalModel';
import { StatusPill, Priority, Avatar, Bar } from './goalBits';

const TABS = [['active', 'Active'], ['all', 'All'], ['completed', 'Completed']];

export default function GoalsSidePanel({ goals, live, currentUserId, selectedId, onOpen, onNew }) {
  const [tab, setTab] = useState('active');

  const notCancelled = goals.filter((g) => goalStatus(g, live) !== 'cancelled');
  const done = goals.filter((g) => goalStatus(g, live) === 'done');
  const active = goals.filter(isOpen);
  const overall = active.length
    ? active.reduce((a, g) => a + (progressOf(g, live)?.pct ?? (goalStatus(g, live) === 'notstarted' ? 0 : 50)), 0) / active.length
    : 100;

  const shown = goals
    .filter((g) => {
      const s = goalStatus(g, live);
      if (tab === 'all') return true;
      if (tab === 'completed') return s === 'done';
      return s !== 'done' && s !== 'cancelled';
    })
    .sort(byDue);

  return (
    <div className="gp2">
      <div className="gp2-head">
        <b>Goals</b>
        <span className="gp2-count" title="Completed out of all goals">{done.length}/{notCancelled.length}</span>
        <button type="button" className="gp2-new" onClick={onNew}>+ New Goal</button>
      </div>
      <div className="gp2-overall" title="Overall progress across active goals" role="presentation">
        <i style={{ width: `${overall}%` }} />
      </div>
      <div className="gp2-tabs" role="tablist">
        {TABS.map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            className={tab === key ? 'is-on' : ''}
            onClick={() => setTab(key)}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="gp2-list">
        {shown.length === 0 && <div className="gp2-empty">No goals here yet.</div>}
        {shown.map((g) => {
          const s = goalStatus(g, live);
          const prog = progressOf(g, live);
          const assignee = (g.assignees || [])[0];
          const assigneeName = (g.assignee_usernames || [])[0];
          return (
            <button
              key={g._id}
              type="button"
              className={`gp2-item${selectedId === g._id ? ' is-on' : ''}`}
              onClick={() => onOpen(g._id)}
            >
              <span className="gp2-title">
                <span className="gp2-tt">{g.name}</span>
                {assignee && String(assignee) !== String(currentUserId) && (
                  <Avatar id={assignee} name={assigneeName} size={20} />
                )}
              </span>
              <span className="gp2-meta">
                <StatusPill status={s} />
                <Priority value={g.priority} onDark={selectedId !== g._id} />
                <span>{s === 'done' ? `Done ${fmtDay(stampDay(g.completed_at))}` : g.due_date ? `Due ${fmtDay(g.due_date)}` : 'No due date'}</span>
              </span>
              <Bar thin pct={prog ? prog.pct : s === 'done' ? 100 : 0} status={s} className="gp2-bar" />
            </button>
          );
        })}
      </div>
    </div>
  );
}
